import React from 'react';
import {
  ChatBubbleLeftRightIcon,
  CheckCircleIcon,
  CloudArrowUpIcon,
  CodeBracketSquareIcon,
  DocumentCheckIcon,
  EnvelopeIcon,
  ExclamationTriangleIcon,
  EyeIcon,
  LockClosedIcon,
  NoSymbolIcon,
  PhotoIcon,
  ShieldCheckIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';
import {
  publicSecurityStatus,
  publicSecurityStatusLabels,
  PublicSecurityStatusLevel,
} from '../security/publicSecurityStatus';

type SecurityCheckItem = {
  title: string;
  description: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
};

const statusToneClasses: Record<PublicSecurityStatusLevel, string> = {
  in_preparation: 'border-primary-dark-300/70 bg-primary-dark-100/55 text-primary-light-200',
  in_review: 'border-gold-300/35 bg-gold-300/10 text-gold-200',
  passed: 'border-ratuj-300/35 bg-ratuj-300/10 text-ratuj-200',
  passed_with_warnings: 'border-gold-300/35 bg-gold-300/10 text-gold-200',
};

const securityChecks: SecurityCheckItem[] = [
  {
    title: 'Role użytkowników',
    description:
      'Sprawdzamy, czy klient, serwis i administrator mają oddzielone uprawnienia, a ważne akcje nie zależą wyłącznie od ukrycia przycisku w interfejsie.',
    icon: UserGroupIcon,
  },
  {
    title: 'Zgłoszenia naprawy',
    description:
      'Kontrolujemy, czy zgłoszenie jest dostępne tylko dla właściwego klienta, przypisanego serwisu lub uprawnionej administracji.',
    icon: DocumentCheckIcon,
  },
  {
    title: 'Zdjęcia i załączniki',
    description:
      'Sprawdzamy, czy pliki przesłane do zgłoszenia nie stają się przypadkowo publiczne i czy ścieżki dostępu są powiązane z właścicielem sprawy.',
    icon: PhotoIcon,
  },
  {
    title: 'Czat i komunikacja',
    description:
      'Weryfikujemy, czy wiadomości są widoczne tylko dla stron danej sprawy i czy nie mieszają się między zgłoszeniami.',
    icon: ChatBubbleLeftRightIcon,
  },
  {
    title: 'Reguły Firebase',
    description:
      'Kontrola obejmuje Firestore Security Rules, Cloud Storage Rules oraz miejsca, w których backend zapisuje dane w imieniu użytkownika.',
    icon: LockClosedIcon,
  },
  {
    title: 'Cloud Functions',
    description:
      'Sprawdzamy, czy funkcje backendowe potwierdzają tożsamość użytkownika, rolę i prawo do wykonania danej akcji.',
    icon: CloudArrowUpIcon,
  },
  {
    title: 'Publiczny profil serwisu',
    description:
      'Oddzielamy informacje publiczne, takie jak opis usług, od prywatnych danych konta, kontaktu, zgłoszeń i ustawień.',
    icon: EyeIcon,
  },
  {
    title: 'Jakość kodu',
    description:
      'Szukamy duplikacji logiki, obejść tymczasowych, fałszywych zabezpieczeń tylko w UI i testów, które nie potwierdzają realnego zachowania.',
    icon: CodeBracketSquareIcon,
  },
];

const gateSteps = [
  {
    title: 'Zmiana w kodzie',
    description: 'Nowa funkcja albo poprawka trafia do sprawdzenia przed wdrożeniem.',
  },
  {
    title: 'Kontrola techniczna',
    description:
      'Sprawdzane są role, reguły dostępu, funkcje backendowe, dane prywatne, pliki i ryzyka typowe dla kodu generowanego z pomocą AI.',
  },
  {
    title: 'Werdykt',
    description: 'Zmiana otrzymuje status: PASS, PASS_WITH_WARNINGS albo BLOCKED.',
  },
  {
    title: 'Działanie',
    description:
      'Przy BLOCKED zmiana nie powinna być wdrażana przed usunięciem problemów. Przy ostrzeżeniach tworzymy zadania naprawcze.',
  },
];

const privateDetails = [
  'nie pokazujemy ścieżek exploitów,',
  'nie pokazujemy prywatnych nazw kolekcji i reguł, jeżeli mogłyby pomóc w ataku,',
  'nie publikujemy szczegółów luk przed ich analizą i poprawą,',
  'nie przechowujemy w publicznym statusie sekretów ani danych prywatnych.',
];

const nonMeanings = [
  'Nie jest to formalne potwierdzenie bezpieczeństwa.',
  'Nie jest to zewnętrzny test penetracyjny.',
  'Nie oznacza braku wszystkich podatności.',
  'Nie zastępuje dalszych testów, monitoringu i poprawek.',
];

const disclosureEmail = 'kontakt@ratujsprzet.pl';
const disclosureMailto = `mailto:${disclosureEmail}?subject=${encodeURIComponent(
  'Bezpieczeństwo Ratuj Sprzęt'
)}`;

const formatReviewDate = (date: string | null) => {
  if (!date) {
    return 'Data ostatniej kontroli: jeszcze nie opublikowano';
  }

  return `Data ostatniej kontroli: ${new Intl.DateTimeFormat('pl-PL', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(new Date(date))}`;
};

const SectionIntro: React.FC<{
  label?: string;
  title: string;
  children?: React.ReactNode;
}> = ({ label, title, children }) => (
  <div className="max-w-3xl">
    {label && <p className="text-sm font-semibold text-ratuj-300">{label}</p>}
    <h2 className="mt-2 text-2xl font-semibold leading-tight text-primary-light-100 sm:text-3xl">
      {title}
    </h2>
    {children && <div className="mt-4 text-base leading-7 text-primary-light-300">{children}</div>}
  </div>
);

const SecurityPage: React.FC = () => {
  const statusLabel = publicSecurityStatusLabels[publicSecurityStatus.level];
  const statusToneClass = statusToneClasses[publicSecurityStatus.level];
  const reviewDateLabel = formatReviewDate(publicSecurityStatus.lastReviewDate);

  return (
    <main className="min-h-screen bg-primary-dark-100 text-primary-light-100">
      <section className="border-b border-primary-dark-300/60 bg-primary-dark-200/42">
        <div className="rs-page-gutter mx-auto grid max-w-6xl gap-8 py-12 sm:py-16 lg:grid-cols-[minmax(0,1.05fr)_minmax(280px,0.62fr)] lg:items-end lg:py-20">
          <div className="max-w-3xl">
            <div className="mb-5 inline-flex h-11 w-11 items-center justify-center rounded-lg border border-ratuj-300/25 bg-ratuj-300/10 text-ratuj-200">
              <ShieldCheckIcon className="h-5 w-5" aria-hidden="true" />
            </div>
            <p className="text-sm font-semibold text-ratuj-300">Bezpieczeństwo i transparentność</p>
            <h1 className="mt-3 text-3xl font-semibold leading-tight text-primary-light-100 sm:text-4xl">
              Nie obiecujemy magii. Pokazujemy proces kontroli.
            </h1>
            <p className="mt-5 max-w-2xl text-base leading-7 text-primary-light-300 sm:text-lg">
              Ratuj Sprzęt przetwarza informacje związane ze zgłoszeniami napraw, kontaktem z
              serwisem, zdjęciami i komunikacją. Dlatego bezpieczeństwo traktujemy jako proces:
              sprawdzamy role, dostęp do danych, reguły Firebase, funkcje backendowe i jakość zmian
              w kodzie.
            </p>
          </div>

          <aside className="rounded-lg border border-primary-dark-300/70 bg-primary-dark-200/70 p-5 shadow-card">
            <p className="text-sm font-semibold text-primary-light-100">Aktualny status</p>
            <div
              className={`mt-4 inline-flex rounded-full border px-3 py-1 text-sm font-semibold ${statusToneClass}`}
            >
              {statusLabel}
            </div>
            <p className="mt-4 text-sm font-medium text-primary-light-100">{reviewDateLabel}</p>
            <p className="mt-3 text-sm leading-6 text-primary-light-300">
              {publicSecurityStatus.notice}
            </p>
          </aside>
        </div>
      </section>

      <div className="rs-page-gutter mx-auto max-w-6xl py-10 sm:py-12 lg:py-14">
        <section>
          <SectionIntro label="Zakres kontroli" title="Co sprawdzamy" />
          <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {securityChecks.map((item) => {
              const Icon = item.icon;

              return (
                <article
                  key={item.title}
                  className="rounded-lg border border-primary-dark-300/65 bg-primary-dark-200/58 p-5"
                >
                  <Icon className="h-5 w-5 text-ratuj-300" aria-hidden="true" />
                  <h3 className="mt-4 text-base font-semibold leading-snug text-primary-light-100">
                    {item.title}
                  </h3>
                  <p className="mt-3 text-sm leading-6 text-primary-light-300">
                    {item.description}
                  </p>
                </article>
              );
            })}
          </div>
        </section>

        <section className="mt-12">
          <SectionIntro label="Security Gate" title="Jak działa Security Gate" />
          <ol className="mt-6 grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            {gateSteps.map((step, index) => (
              <li
                key={step.title}
                className="rounded-lg border border-primary-dark-300/65 bg-primary-dark-200/58 p-5"
              >
                <span className="flex h-8 w-8 items-center justify-center rounded-lg border border-ratuj-300/25 bg-ratuj-300/10 text-sm font-semibold text-ratuj-200">
                  {index + 1}
                </span>
                <h3 className="mt-4 text-base font-semibold leading-snug text-primary-light-100">
                  {step.title}
                </h3>
                <p className="mt-3 text-sm leading-6 text-primary-light-300">{step.description}</p>
              </li>
            ))}
          </ol>
        </section>

        <section className="mt-12 grid gap-6 rounded-lg border border-primary-dark-300/65 bg-primary-dark-200/58 p-5 sm:p-6 lg:grid-cols-[0.9fr_1.1fr]">
          <SectionIntro title="Czego publicznie nie pokazujemy">
            <p>
              Nie publikujemy pełnych raportów technicznych, dokładnych scenariuszy ataku ani
              informacji o potencjalnych lukach przed ich naprawą. Taka transparentność byłaby
              pozorna - mogłaby ułatwić atak zamiast pomóc użytkownikom.
            </p>
          </SectionIntro>
          <ul className="grid gap-3 text-sm leading-6 text-primary-light-300 sm:grid-cols-2 lg:grid-cols-1">
            {privateDetails.map((item) => (
              <li key={item} className="flex gap-3">
                <CheckCircleIcon
                  className="mt-0.5 h-5 w-5 flex-none text-ratuj-300"
                  aria-hidden="true"
                />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </section>

        <section className="mt-12 grid gap-5 lg:grid-cols-[1fr_0.86fr]">
          <div className="rounded-lg border border-primary-dark-300/65 bg-primary-dark-200/58 p-5 sm:p-6">
            <EnvelopeIcon className="h-6 w-6 text-ratuj-300" aria-hidden="true" />
            <h2 className="mt-4 text-xl font-semibold text-primary-light-100">
              Zgłaszanie problemów bezpieczeństwa
            </h2>
            <p className="mt-3 text-sm leading-6 text-primary-light-300">
              Jeżeli zauważysz potencjalny problem bezpieczeństwa, zgłoś go prywatnie. Nie publikuj
              szczegółów publicznie przed analizą - to pomaga chronić użytkowników i serwisy.
            </p>
            <a
              href={disclosureMailto}
              className="mt-5 inline-flex min-h-[44px] items-center justify-center rounded-lg border border-ratuj-300/35 bg-ratuj-300/10 px-4 py-2 text-sm font-semibold text-ratuj-200 transition-colors hover:bg-ratuj-300/15 focus-outline"
            >
              Napisz do nas
            </a>
            <p className="mt-4 text-sm leading-6 text-primary-light-300">
              Kontakt: <span className="font-medium text-primary-light-100">{disclosureEmail}</span>
            </p>
            <p className="mt-2 text-sm leading-6 text-primary-light-400">
              W tytule wiadomości wpisz: Bezpieczeństwo Ratuj Sprzęt.
            </p>
          </div>

          <div className="rounded-lg border border-primary-dark-300/65 bg-primary-dark-200/58 p-5 sm:p-6">
            <NoSymbolIcon className="h-6 w-6 text-gold-300" aria-hidden="true" />
            <h2 className="mt-4 text-xl font-semibold text-primary-light-100">
              Czego ten status nie oznacza
            </h2>
            <ul className="mt-4 space-y-3 text-sm leading-6 text-primary-light-300">
              {nonMeanings.map((item) => (
                <li key={item} className="flex gap-3">
                  <ExclamationTriangleIcon
                    className="mt-0.5 h-4 w-4 flex-none text-gold-300"
                    aria-hidden="true"
                  />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>
      </div>
    </main>
  );
};

export default SecurityPage;
